// Track C / Batch C2 — the kiosk flow as a PURE state machine.
//
//   idle (QR) ──START (PIN fallback)──▶ pin ──PIN_SUBMIT──▶ direction ──PUNCH──▶ submitting
//                                                            │
//                              ┌─ PUNCH_OK ──▶ success ──────┤
//                              └─ PUNCH_FAIL ─▶ failure ◀────┘
//   success/failure ──RESET (Tamam / 5s auto-return)──▶ idle
//   definitive failure ──RETRY──▶ pin (cleared)
//   ambiguous failure ──RETRY──▶ submitting (frozen intent replay)
//
// The reducer is side-effect free (timers + transport calls live in App)
// so every transition is unit-testable. PUNCH_OK / PUNCH_FAIL are ignored
// outside the submitting step — a late transport resolution after RESET
// can never resurrect a dead screen.

import type { Classification } from "@novemai/error-envelope";

import type { KioskPunchRequest, KioskPunchResult, PunchDirection } from "@/api/punch";

export const PIN_MIN_LENGTH = 6;
export const PIN_MAX_LENGTH = 6;

/** success/failure screens auto-return to idle after this long. */
export const AUTO_RETURN_MS = 5000;

/** 4857 kiosk privacy boundary: clear an unattended employee PIN/choice. */
export const KIOSK_INACTIVITY_MS = 45_000;

export type KioskState =
  | { readonly step: "idle" }
  | { readonly step: "pin"; readonly pin: string }
  | { readonly step: "direction"; readonly pin: string }
  | {
      readonly step: "submitting";
      readonly pin: string;
      readonly direction: PunchDirection;
    }
  | { readonly step: "success"; readonly result: KioskPunchResult }
  | {
      readonly step: "failure";
      readonly messageKey: string;
      readonly classification: Classification | undefined;
      readonly retryable?: boolean | undefined;
      readonly retryAfterMs?: number | undefined;
      readonly traceId?: string | undefined;
      /** Only an ambiguous transport failure may replay this frozen request. */
      readonly retryIntent?: KioskPunchRequest | undefined;
    };

export type KioskEvent =
  | { readonly type: "START" }
  | { readonly type: "DIGIT"; readonly digit: string }
  | { readonly type: "BACKSPACE" }
  | { readonly type: "PIN_SUBMIT" }
  | { readonly type: "BACK" }
  | { readonly type: "PUNCH"; readonly direction: PunchDirection }
  | { readonly type: "PUNCH_OK"; readonly result: KioskPunchResult }
  | {
      readonly type: "PUNCH_FAIL";
      readonly messageKey: string;
      readonly classification: Classification | undefined;
      readonly retryable?: boolean | undefined;
      readonly retryAfterMs?: number | undefined;
      readonly traceId?: string | undefined;
      readonly retryIntent?: KioskPunchRequest | undefined;
    }
  | { readonly type: "RETRY" }
  | { readonly type: "RESET" };

export const KIOSK_INITIAL_STATE: KioskState = { step: "idle" };

export function canSubmitPin(state: KioskState): boolean {
  return state.step === "pin" && state.pin.length === PIN_MIN_LENGTH;
}

export function kioskReducer(state: KioskState, event: KioskEvent): KioskState {
  switch (event.type) {
    case "START":
      return state.step === "idle" ? { step: "pin", pin: "" } : state;

    case "DIGIT": {
      if (state.step !== "pin") return state;
      if (!/^\d$/.test(event.digit)) return state;
      if (state.pin.length >= PIN_MAX_LENGTH) return state;
      return { step: "pin", pin: state.pin + event.digit };
    }

    case "BACKSPACE":
      if (state.step !== "pin") return state;
      return { step: "pin", pin: state.pin.slice(0, -1) };

    case "PIN_SUBMIT":
      if (!canSubmitPin(state) || state.step !== "pin") return state;
      return { step: "direction", pin: state.pin };

    case "BACK":
      if (state.step === "pin") return { step: "idle" };
      if (state.step === "direction") return { step: "pin", pin: state.pin };
      return state;

    case "PUNCH":
      if (state.step !== "direction") return state;
      return { step: "submitting", pin: state.pin, direction: event.direction };

    case "PUNCH_OK":
      if (state.step !== "submitting") return state;
      return { step: "success", result: event.result };

    case "PUNCH_FAIL":
      if (state.step !== "submitting") return state;
      return {
        step: "failure",
        messageKey: event.messageKey,
        classification: event.classification,
        ...(event.retryable !== undefined && { retryable: event.retryable }),
        ...(event.retryAfterMs !== undefined && { retryAfterMs: event.retryAfterMs }),
        ...(event.traceId !== undefined && { traceId: event.traceId }),
        ...(event.retryIntent !== undefined && { retryIntent: event.retryIntent }),
      };

    case "RETRY":
      if (state.step !== "failure") return state;
      if (state.retryIntent !== undefined) {
        return {
          step: "submitting",
          pin: state.retryIntent.pin,
          direction: state.retryIntent.direction,
        };
      }
      return { step: "pin", pin: "" };

    case "RESET":
      return { step: "idle" };
  }
}
